import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import s from '../../pages/PDV.module.css';
import { calcularItem, lerNumero, precoDoCampo, precoMinimo } from '../../utils/descontoItemPdv';

const fmt = (v, casas = 2) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

const rotulo = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: '#334155' };
const campoBase = { padding: '6px 8px', fontSize: 15, textAlign: 'right', border: '1px solid #94a3b8', borderRadius: 3, width: '100%', boxSizing: 'border-box', color: '#111827', background: '#fff' };
const campoLeitura = { ...campoBase, background: '#eef2f6', color: '#334155' };
const botao = { padding: '6px 18px', fontSize: 13, cursor: 'pointer', border: '1px solid #94a3b8', borderRadius: 3, background: '#f1f5f9' };

// Item da venda: desconto em % ou R$, preço, quantidade e total (Parâmetros →
// PDV → "Permitir desconto unitário"). Enter confirma e Esc sai. As teclas ficam
// presas aqui: os atalhos do PDV (Delete, F9, Enter...) não agem por trás.
export default function ModalItemPdv({ item, onConfirmar, onFechar }) {
  // Administrador não passa pela verificação do desconto máximo (vendasController.criar).
  const ehAdmin = String(useAuth()?.usuario?.perfil || '').toLowerCase() === 'admin';
  const bruto = Number(item.preco_tabela ?? item.preco_unit) || 0;
  const [qtd, setQtd] = useState(Number(item.qtd) || 0);
  const [unit, setUnit] = useState(Number(item.preco_unit) || 0);
  const [editando, setEditando] = useState(null); // { campo, texto } do campo em digitação
  const [aviso, setAviso] = useState('');
  const [maxPct, setMaxPct] = useState(undefined); // undefined = não carregou
  const refDesconto = useRef(null);

  useEffect(() => {
    refDesconto.current?.focus();
    refDesconto.current?.select();
    let ativo = true;
    api.get(`/produtos/${item.id}`)
      .then(({ data }) => { if (ativo) setMaxPct(Number(data?.desconto_maximo_pct) || 0); })
      .catch(() => { /* sem conexão: o limite continua valendo ao finalizar a venda */ });
    return () => { ativo = false; };
  }, [item.id]);

  const v = calcularItem({ bruto, qtd, unit });
  const minimo = maxPct === undefined ? undefined : precoMinimo(bruto, maxPct);
  const podeConfirmar = qtd > 0 && unit > 0;

  const atual = useRef();
  atual.current = { podeConfirmar, qtd, unit, total: v.total };
  const confirmar = () => {
    const a = atual.current;
    if (!a.podeConfirmar) return;
    onConfirmar({ qtd: a.qtd, preco_unit: a.unit, total: a.total, preco_manual: true });
  };

  useEffect(() => {
    const aoTeclar = e => {
      // Campo "Qtd." etc. continuam recebendo a digitação: só a propagação para.
      e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); onFechar(); return; }
      if (e.key === 'Enter' && e.target?.tagName !== 'BUTTON') { e.preventDefault(); confirmar(); }
      // Teclas de função são atalhos do navegador/PDV: não valem com o modal aberto.
      if (/^F\d+$/.test(e.key)) e.preventDefault();
    };
    window.addEventListener('keydown', aoTeclar, true);
    return () => window.removeEventListener('keydown', aoTeclar, true);
  }, []); // eslint-disable-line

  const digitar = (campo, texto) => {
    setEditando({ campo, texto });
    if (campo === 'qtd') { setQtd(Math.round(lerNumero(texto) * 1000) / 1000); return; }
    setUnit(precoDoCampo(campo, texto, { bruto, qtd, unit }));
  };

  const sair = campo => {
    if (campo === 'total' && editando?.campo === 'total') {
      const digitado = lerNumero(editando.texto);
      setAviso(Math.abs(digitado - v.total) >= 0.005
        ? `O preço unitário tem 2 casas: com ${fmt(qtd, 3)} ${item.un || ''}, o total mais próximo é R$ ${fmt(v.total)}.`
        : '');
    } else if (editando) setAviso('');
    setEditando(null);
  };

  const texto = (campo, valor, casas) => (editando?.campo === campo ? editando.texto : fmt(valor, casas));
  const props = (campo, valor, casas) => ({
    style: campoBase, inputMode: 'decimal', value: texto(campo, valor, casas),
    onChange: e => digitar(campo, e.target.value),
    onFocus: e => e.target.select(),
    onBlur: () => sair(campo),
  });

  return (
    <div className={s.modalOverlay} data-enter-scope="off" onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Item" style={{
        background: '#fff', borderRadius: 6, width: 420, maxWidth: '94vw', overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.35)', fontFamily: 'Tahoma, sans-serif',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f1f5f9', fontWeight: 'bold', fontSize: 13, color: '#1e293b' }}>
          <span>Item</span>
          <button type="button" aria-label="Fechar" onClick={onFechar} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 15 }}>✕</button>
        </div>
        <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 70px', gap: 8 }}>
            <label style={rotulo}>Descrição:
              <input readOnly tabIndex={-1} value={item.descricao || ''} style={{ ...campoLeitura, textAlign: 'left', fontSize: 13 }} />
            </label>
            <label style={rotulo}>UN:
              <input readOnly tabIndex={-1} value={item.un || ''} style={{ ...campoLeitura, textAlign: 'left', fontSize: 13 }} />
            </label>
          </div>
          <label style={{ ...rotulo, gridColumn: '1 / 2' }}>Preço bruto R$:
            <input readOnly tabIndex={-1} value={fmt(bruto)} style={campoLeitura} />
          </label>
          <span />
          <label style={rotulo}>Desconto %:
            <input ref={refDesconto} {...props('pct', v.pct, 5)} />
          </label>
          <label style={rotulo}>Desc. R$:
            <input {...props('descR', v.descR, 2)} />
          </label>
          <label style={rotulo}>Preço unit. R$:
            <input {...props('unit', unit, 2)} />
          </label>
          <label style={rotulo}>Mín. R$:
            <input readOnly tabIndex={-1} style={campoLeitura}
              value={minimo === undefined ? '—' : minimo === null ? 'Sem limite' : fmt(minimo)} />
          </label>
          <label style={rotulo}>Qtd.:
            <input {...props('qtd', qtd, 3)} />
          </label>
          <label style={rotulo}>Total R$:
            <input {...props('total', v.total, 2)} />
          </label>
          {aviso && <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#92400e' }}>{aviso}</div>}
          {!ehAdmin && minimo != null && unit < minimo && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#b91c1c' }}>
              Abaixo do mínimo de R$ {fmt(minimo)}: a venda vai pedir a senha de administrador ao finalizar.
            </div>
          )}
          {!podeConfirmar && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#b91c1c' }}>Quantidade e preço precisam ser maiores que zero.</div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 14px', borderTop: '1px solid #e2e8f0' }}>
          <button type="button" style={botao} disabled={!podeConfirmar} onClick={confirmar}>Ok</button>
          <button type="button" style={botao} onClick={onFechar}>Sair</button>
        </div>
      </div>
    </div>
  );
}
